/**
 * The report's vendor groups start folded (a report reads as a rollup first),
 * so what gets remembered is the *expanded* set rather than the collapsed one:
 * a fresh visitor — or a brand-new group — lands collapsed, and only what the
 * user actually opened is persisted. Kept in localStorage rather than the URL:
 * it is view state, not a filter, and it should not turn a shared report link
 * into an opinion about which groups are interesting.
 */

import { useCallback, useEffect, useState } from 'react';

import { decodeExpandedGroups, encodeExpandedGroups } from './report-groups';

/** A new key, not the old collapsed one: an old `[]` would mean "all open". */
const STORAGE_KEY = 'reports.expandedGroups';

function readInitial(): Set<string> {
  try {
    return decodeExpandedGroups(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Storage can be unavailable (private mode, disabled cookies) — the
    // expand state then just lives for the session.
    return new Set();
  }
}

export function useExpandedGroups(): { expanded: Set<string>; toggle: (name: string) => void } {
  const [expanded, setExpanded] = useState<Set<string>>(readInitial);

  // One writer, one effect: no side effects inside the state updater, which
  // StrictMode is free to call twice.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, encodeExpandedGroups(expanded));
    } catch {
      // Unpersistable is not unusable — keep the in-memory state.
    }
  }, [expanded]);

  const toggle = useCallback((name: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  return { expanded, toggle };
}
